"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { CreditBalanceBadge } from "@/components/ui/credit-balance";
import { useCustomOrderPermissions } from "@/hooks/useCustomOrderPermissions";

interface BreadcrumbChild {
  href: string;
  label: string;
  /** Needs the Custom Order `manage` permission; listed but disabled for everyone else. */
  dataTeamOnly?: boolean;
}

interface BreadcrumbItem {
  label: string;
  href?: string;
  children?: BreadcrumbChild[];
}

const NAVIGATION_STRUCTURE: { label: string; children: BreadcrumbChild[] }[] = [
  {
    label: "Dashboard",
    children: [{ href: "/", label: "Dashboard" }],
  },
  {
    label: "Search",
    children: [
      { href: "/search/people", label: "People" },
      { href: "/search/companies", label: "Companies" },
    ],
  },
  {
    label: "Lists",
    children: [
      { href: "/lists/contacts", label: "Contact List" },
      { href: "/lists/email-suppression", label: "Email Suppression" },
      { href: "/lists/name-suppression", label: "Name Suppression" },
      { href: "/lists/website-suppression", label: "Website Suppression" },
    ],
  },
  {
    label: "Enrichment",
    children: [{ href: "/enrichment", label: "Enrichment" }],
  },
  {
    label: "Custom Data",
    children: [
      { href: "/custom-data/new", label: "New Order" },
      { href: "/custom-data/orders", label: "My Orders" },
      { href: "/custom-data/manage", label: "Manage Orders", dataTeamOnly: true },
    ],
  },
  {
    label: "Settings",
    children: [
      { href: "/settings", label: "Settings" },
      { href: "/settings/profile", label: "Profile" },
      { href: "/settings/usage", label: "Usage" },
      { href: "/settings/users", label: "Team" },
      { href: "/settings/billing", label: "Billing" },
      { href: "/settings/pricing", label: "Pricing" },
      { href: "/settings/exports", label: "Exports" },
    ],
  },
  {
    label: "Company Details",
    children: [{ href: "/company-details", label: "Companies" }],
  },
];

function getBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const paths = pathname.split("/").filter(Boolean);
  const breadcrumbs: BreadcrumbItem[] = [];

  const parentSection = NAVIGATION_STRUCTURE.find((section) =>
    section.children &&
    section.children.some((child) => child.href.startsWith(`/${paths[0]}`))
  );

  if (parentSection) {
    breadcrumbs.push({
      label: parentSection.label,
      href: parentSection.children?.[0]?.href,
      children: parentSection.children,
    });

    if (paths.length > 1) {
      const fullPath = `/${paths.join("/")}`;
      const child =
        parentSection.children?.find(
          (child) =>
            child.href === fullPath ||
            child.href.endsWith(paths[paths.length - 1])
        ) ??
        // Detail pages (e.g. /custom-data/manage/<id>) fall back to the
        // deepest section they sit under. Top-level hrefs like /settings are
        // skipped, or /settings/xyz would read "Settings / Settings".
        parentSection.children
          ?.filter(
            (child) =>
              child.href.split("/").length > 2 &&
              fullPath.startsWith(`${child.href}/`)
          )
          .sort((a, b) => b.href.length - a.href.length)[0];

      if (child) {
        breadcrumbs.push({
          label: child.label,
          href: child.href,
          children: parentSection.children,
        });
      }
    }
  } else {
    let fullPath = "";
    paths.forEach((segment) => {
      fullPath += `/${segment}`;
      breadcrumbs.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1),
        href: fullPath,
      });
    });
  }

  return breadcrumbs;
}

export default function GlobalBreadcrumbs() {
  const pathname = usePathname();
  const router = useRouter();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { canManage: isDataTeam } = useCustomOrderPermissions();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const breadcrumbs = useMemo(() => getBreadcrumbs(pathname), [pathname]);

  const hiddenRoutes = ["/auth/login", "/auth/register", "/user-profile"];
  const isAuthRoute = pathname?.startsWith("/auth");
  const isHidden = isAuthRoute || hiddenRoutes.includes(pathname || "");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  if (isHidden || !breadcrumbs.length) return null;

  return (
    <div className="w-full border-b border-border bg-background px-4 py-2 flex items-center justify-between z-10">
      <nav
        className="flex items-center pointer-events-auto"
        aria-label="breadcrumb"
      >
        <ol className="flex items-center m-0 p-0 list-none">
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            const key = `${crumb.href || crumb.label}-${idx}`;

            if (isLast && crumb.children && crumb.children.length > 1) {
              return (
                <li
                  key={key}
                  className="flex items-center text-sm text-muted-foreground relative"
                >
                  <div
                    className="flex items-center relative"
                    ref={dropdownRef}
                  >
                    <button
                      className={cn(
                        "bg-none border-none p-1 cursor-pointer flex items-center transition-colors duration-200",
                        "text-foreground hover:text-foreground"
                      )}
                      aria-label={`Show ${crumb.label} options`}
                      onClick={() => setDropdownOpen((open) => !open)}
                    >
                      <span className="text-foreground font-medium text-base">
                        {crumb.label}
                      </span>
                      <ChevronDown className="ml-1 h-3 w-3" />
                    </button>
                    {dropdownOpen && (
                      <div className="absolute z-10001 min-w-40 py-2 px-0 mt-0.5 bg-background border border-border rounded-sm shadow-lg top-full left-0 -translate-x-3 translate-y-0.5 text-muted-foreground pointer-events-auto">
                        {crumb.children
                          .filter((child) => child.href !== crumb.href)
                          .filter((child) => !child.dataTeamOnly || isDataTeam)
                          .map((child, childIdx) => (
                            <Link
                              key={`${child.href}-${childIdx}`}
                              href={child.href}
                              className="block w-11/12 px-4 py-2 font-normal text-foreground no-underline whitespace-nowrap bg-transparent border-none cursor-pointer ml-2 hover:bg-muted transition-colors duration-200"
                              onClick={() => setDropdownOpen(false)}
                            >
                              {child.label}
                            </Link>
                          ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            }

            if (isLast) {
              return (
                <li
                  key={key}
                  className="flex items-center text-sm text-muted-foreground relative"
                >
                  <div className="flex items-center text-foreground font-medium text-base">
                    {crumb.label}
                  </div>
                </li>
              );
            }

            return (
              <li
                key={key}
                className={cn(
                  "flex items-center text-sm text-muted-foreground",
                  !isLast && "after:content-['/'] after:mx-2 after:text-muted-foreground"
                )}
              >
                <div className="flex items-center relative">
                  <Link
                    href={crumb.href || "#"}
                    className="text-muted-foreground no-underline transition-colors duration-200 hover:text-foreground"
                  >
                    {crumb.label}
                  </Link>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Available credits — always visible on the top bar */}
      <CreditBalanceBadge />
    </div>
  );
}
